import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Palette, PlugZap, Unplug, Loader2, Check, Copy, Lock, Search, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtRel } from "@/lib/api";
import { Pill, Dot } from "@/components/app/shared";
import { cn } from "@/lib/utils";

function FieldCheck({ dataset, maxItems }) {
  if (!dataset) return null;
  const matched = new Set(dataset.matched || []);
  const base = ["cliente", "contacto", "numero", "fecha", "vigencia", "subtotal", "descuento", "iva", "total", "terminos"];
  return (
    <div className="space-y-2 rounded-md border border-border bg-background p-3" data-testid="canva-field-check">
      <div className="text-[12px] font-medium">
        {dataset.missingRequired?.length ? "Faltan campos obligatorios en la plantilla" : `Plantilla lista · ${dataset.itemSlots} espacios de producto`}
      </div>
      <div className="flex flex-wrap gap-1">
        {base.map((f) => (
          <span key={f} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10.5px]",
            matched.has(f) ? "border-mint bg-mint/50" : "border-border text-muted-foreground")}>
            {matched.has(f) ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{f}
          </span>
        ))}
      </div>
      <div className="text-[11px] text-muted-foreground">
        Productos: producto_N, cantidad_N, precio_N, total_N (N = 1 a {maxItems}). Detectados: {dataset.itemSlots || 0}.
        {dataset.unknown?.length ? ` Campos que el Hub no llena: ${dataset.unknown.join(", ")}.` : ""}
      </div>
    </div>
  );
}

export default function CanvaCard({ canManage }) {
  const qc = useQueryClient();
  const [ok, setOk] = useState(false);
  const [query, setQuery] = useState("");
  const [dataset, setDataset] = useState(null);
  const { data: st, isLoading, refetch } = useQuery({ queryKey: ["canva-status"], queryFn: async () => (await api.get("/canva/status")).data });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const c = p.get("canva");
    if (!c) return;
    if (c === "connected") toast.success("Canva conectado");
    else toast.error("No se pudo conectar Canva. Inténtalo de nuevo.");
    p.delete("canva");
    window.history.replaceState({}, "", `${window.location.pathname}${p.toString() ? `?${p}` : ""}`);
    refetch();
  }, [refetch]);

  const templates = useQuery({
    queryKey: ["canva-templates", query],
    queryFn: async () => (await api.get(`/canva/templates${query ? `?query=${encodeURIComponent(query)}` : ""}`)).data,
    enabled: !!st?.connected && canManage,
  });
  useQuery({
    queryKey: ["canva-dataset", st?.templateId],
    queryFn: async () => { const d = (await api.get("/canva/template/dataset")).data; setDataset(d); return d; },
    enabled: !!st?.connected && !!st?.templateId,
  });

  const connect = useMutation({
    mutationFn: async () => (await api.get("/canva/oauth/start")).data,
    onSuccess: (r) => { if (r?.url) window.location.href = r.url; },
    onError: (e) => toast.error(errMsg(e)),
  });
  const disconnect = useMutation({
    mutationFn: async () => api.post("/canva/disconnect"),
    onSuccess: () => { toast.success("Canva desconectado"); refetch(); },
  });
  const choose = useMutation({
    mutationFn: async (t) => (await api.put("/canva/template", { templateId: t.id, title: t.title })).data,
    onSuccess: (r) => { setDataset(r.dataset); toast.success("Plantilla guardada"); qc.invalidateQueries({ queryKey: ["canva-status"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (isLoading || !st) return <div className="h-40 animate-pulse rounded-lg border border-border bg-card" />;

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="canva-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold"><Palette className="h-4 w-4" /> Canva · PDF de propuestas con marca</div>
          <div className="text-xs text-muted-foreground">Una cuenta Canva Pro de Mediana. Sin Canva, el Hub usa el diseño Mediana automáticamente.</div>
        </div>
        <Pill tone={st.connected ? "mint" : st.configured ? "neutral" : "butter"} data-testid="canva-status-pill">
          <Dot tone={st.connected ? "ok" : st.configured ? "warn" : "muted"} />
          {st.connected ? "Conectado" : st.configured ? "Listo para conectar" : "Pendiente de configurar"}
        </Pill>
      </div>

      {!st.configured && (
        <div className="border-b border-border bg-butter/60 px-5 py-3 text-[12.5px]" data-testid="canva-not-configured">
          <div className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Faltan CANVA_CLIENT_ID y CANVA_CLIENT_SECRET.</div>
          <div className="mt-1 text-[11.5px] opacity-80">Mientras tanto, cada propuesta se genera con el PDF de diseño Mediana (logo, colores y tipografía de la revista).</div>
        </div>
      )}

      <div className="grid gap-5 p-5 md:grid-cols-2">
        <div className="space-y-3">
          <div className="kicker">Cuenta</div>
          {st.connected ? (
            <div className="rounded-md border border-border bg-background p-3 text-[12.5px]">
              Conectada por <span className="font-mono text-[11.5px]">{st.connectedBy}</span> · {fmtRel(st.connectedAt)}
            </div>
          ) : st.lastError ? (
            <div className="rounded-md border border-blush bg-blush/40 p-3 text-[12px]">{st.lastError}</div>
          ) : null}
          {canManage && (
            st.connected ? (
              <Button variant="ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending} data-testid="canva-disconnect"><Unplug className="mr-1.5 h-4 w-4" /> Desconectar</Button>
            ) : (
              <Button onClick={() => connect.mutate()} disabled={!st.configured || connect.isPending} data-testid="canva-connect">
                {connect.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlugZap className="mr-1.5 h-4 w-4" />} Conectar Canva
              </Button>
            )
          )}
          <div className="kicker pt-2">Redirect URL para Canva Developer Portal</div>
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5" data-testid="canva-redirect-uri">
            <div className="flex-1 truncate font-mono text-[11px]">{st.redirectUri}</div>
            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Copiar" onClick={() => { navigator.clipboard?.writeText(st.redirectUri); setOk(true); setTimeout(() => setOk(false), 1200); }}>
              {ok ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="kicker">Plantilla de marca</div>
          {st.templateId ? (
            <div className="text-[12.5px]" data-testid="canva-template-current">
              <span className="font-medium">{st.templateTitle || "Plantilla"}</span> <span className="font-mono text-[10.5px] text-muted-foreground">{st.templateId}</span>
            </div>
          ) : (
            <div className="text-[12px] text-muted-foreground">
              Crea en Canva una plantilla de marca con campos de datos: cliente, numero, fecha, total, iva, subtotal y producto_1…producto_{st.maxItems} (con cantidad_N, precio_N y total_N).
            </div>
          )}
          <FieldCheck dataset={dataset} maxItems={st.maxItems} />
          {st.connected && canManage && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar plantillas de marca…" className="pl-8" data-testid="canva-template-search" />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-border">
                {templates.isLoading ? (
                  <div className="flex items-center justify-center p-4 text-xs text-muted-foreground"><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Cargando…</div>
                ) : templates.error ? (
                  <div className="p-3 text-xs text-muted-foreground">{errMsg(templates.error)}</div>
                ) : (templates.data || []).length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground">No hay plantillas de marca en esta cuenta.</div>
                ) : (
                  <ul className="divide-y divide-border">
                    {templates.data.map((t) => (
                      <li key={t.id}>
                        <button onClick={() => choose.mutate(t)} disabled={choose.isPending}
                          className={cn("flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent", t.id === st.templateId && "bg-lilac/30")}
                          data-testid="canva-template-item">
                          {t.thumbnail ? <img src={t.thumbnail} alt="" className="h-9 w-12 rounded object-cover" /> : <div className="h-9 w-12 rounded bg-muted" />}
                          <span className="flex-1 truncate text-[12.5px]">{t.title}</span>
                          {t.id === st.templateId && <Check className="h-4 w-4" />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
